import {
  IDestinationEntityJobRunOptions,
  type IDestinationEntity,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  BASIC_MEDICAL_HISTORY_FORM_SCHEMA,
  Brand,
  CustomFormConfiguration,
  CustomFormContent,
  toMedicalHistoryForm,
} from '@principle-theorem/principle-core';
import {
  snapshot,
  snapshotCombineLatest,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { get, sortBy } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  BasePatientMedicalHistoryDestinationEntity,
  IPatientMedicalHistoryFormSubmission,
  IPatientMedicalHistoryJobData,
} from '../../../destination/entities/patient-medical-histories';
import {
  getValidMedicalHistoryFormOptions,
  setCustomFormValue,
} from '../../../mappings/allergy-to-medical-history';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import { PatientAllergySourceEntity } from '../../source/entities/patient-allergies';
import { PatientMedicalConditionSourceEntity } from '../../source/entities/patient-medical-conditions';
import {
  CORE_PRACTICE_PATIENT_MEDICAL_HISTORY_MAPPING,
  PatientMedicalHistorySourceEntity,
} from '../../source/entities/patient-medical-histories';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { CorePracticeAllergyMappingHandler } from '../mappings/allergy-to-medical-history';
import { CorePracticeMedicalConditionMappingHandler } from '../mappings/medical-condition-to-medical-history';
import { PatientDestinationEntity } from './patients';

type JobData = IPatientMedicalHistoryJobData<CorePracticePatient>;

export class PatientMedicalHistoryDestinationEntity extends BasePatientMedicalHistoryDestinationEntity<
  CorePracticePatient,
  JobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    medicalHistories: new PatientMedicalHistorySourceEntity(),
    medicalConditions: new PatientMedicalConditionSourceEntity(),
    allergies: new PatientAllergySourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    medicalConditions: new CorePracticeMedicalConditionMappingHandler(),
    allergies: new CorePracticeAllergyMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const medicalConditions$ =
      this.customMappings.medicalConditions.getRecords(translationMap);
    const allergies$ = this.customMappings.allergies.getRecords(translationMap);

    return snapshotCombineLatest([brand$, medicalConditions$, allergies$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [brand, medicalConditions, allergies]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          brand,
          medicalConditions,
          allergies,
        }))
      )
    );
  }

  async buildFormSubmission(
    migration: WithRef<IPracticeMigration>,
    data: JobData
  ): Promise<IPatientMedicalHistoryFormSubmission | undefined> {
    const sourcePatientId = data.sourcePatient.data.id;
    const medicalHistories =
      await this.sourceEntities.medicalHistories.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

    const medicalHistory = sortBy(
      medicalHistories.filter((history) => !history.data.isDeleted),
      (history) => history.data.dateAttend
    ).pop();

    const sourceMedicalConditions = medicalHistory
      ? await this.sourceEntities.medicalConditions.filterRecords(
          migration,
          'patientHistoryId',
          medicalHistory.data.id
        )
      : [];

    const sourceAllergies = medicalHistory
      ? await this.sourceEntities.allergies.filterRecords(
          migration,
          'patientHistoryId',
          medicalHistory.data.id
        )
      : [];

    const config = await snapshot(Brand.medicalHistoryFormConfig$(data.brand));
    const content = config
      ? await CustomFormConfiguration.getContent(config)
      : undefined;
    const jsonSchemaForm = content
      ? CustomFormContent.getJsonSchemaForm(content)
      : {};
    const form = toMedicalHistoryForm(jsonSchemaForm);
    const schema = form?.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
    let formData = {};

    getValidMedicalHistoryFormOptions(schema).map((formOption) => {
      const mappedAllergy = data.allergies.find(
        (allergy) => allergy.destinationValue?.value === formOption.path
      );

      const patientHasAllergy = sourceAllergies.find(
        (sourceAllergy) =>
          mappedAllergy?.sourceIdentifier === sourceAllergy.data.description
      );

      if (patientHasAllergy && mappedAllergy?.destinationValue) {
        formData = setCustomFormValue(
          formOption,
          formData,
          true,
          mappedAllergy.associatedValue?.toString(),
          patientHasAllergy.data.description
        );
        return;
      }

      const mappedMedicalCondition = data.medicalConditions.find(
        (medicalCondition) =>
          medicalCondition.destinationValue?.value === formOption.path
      );

      const patientHasMedicalCondition = sourceMedicalConditions.find(
        (sourceMedicalCondition) =>
          mappedMedicalCondition?.sourceIdentifier ===
          sourceMedicalCondition.data.description
      );

      if (
        patientHasMedicalCondition &&
        mappedMedicalCondition?.destinationValue
      ) {
        formData = setCustomFormValue(
          formOption,
          formData,
          true,
          mappedMedicalCondition.associatedValue?.toString(),
          patientHasMedicalCondition.data.description
        );
        return;
      }

      if (medicalHistory) {
        Object.entries(CORE_PRACTICE_PATIENT_MEDICAL_HISTORY_MAPPING)
          .filter(([_, value]) => value === formOption.path)
          .map(([key]) => {
            formData = setCustomFormValue(
              formOption,
              formData,
              true,
              undefined,
              get(medicalHistory.data, key) as string
            );
          });
      }

      if (get(formData, formOption.path, undefined)) {
        return;
      }

      formData = setCustomFormValue(formOption, formData);
    });

    return {
      ...form,
      data: formData,
      date: medicalHistory?.record.translations.dateAttend || toTimestamp(),
      disableChanges: true,
    };
  }
}
